

import { useState, useEffect } from "react";
import MainLayouts from "../../../Layout/MainLayouts";
import { ReactComponent as ArrowLeftSquare } from "bootstrap-icons/icons/arrow-left-square-fill.svg";
import Spinner from "../../spinner/Spinner";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import ApiServices from "../../../api/ApiServices";
import { toast } from "react-toastify";
import { handleError } from "../../../components/utils/AuthenticationCheck";
import CurrencyFormatter from "../../../components/utils/CurrencyFormatter";

const BranchDetails = () => {
  const [branchDetails, setBranchDetails] = useState({});
  const [branch_id, setBranchId] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};
  const { branchData } = location.state || {};

  useEffect(() => {
    if (id) setBranchId(id);
  }, []);

  useEffect(() => {
    if (branchData) {
      // Map the data using your structure
      const mappedData = {
        name: branchData.name,
        phone_number: branchData.phone_number,
        is_activated: branchData.is_activated || "NOT AVAILABLE",
        state: branchData.state || "NOT AVAILABLE",
        city: branchData.city || "NOT AVAILABLE",
        status: branchData.status || "NOT AVAILABLE",
        creator: branchData.creator || "NOT AVAILABLE",
        email: branchData.email || "NOT AVAILABLE",
        created_at: branchData.created_at || "NOT AVAILABLE",
        id: branchData.id || "NOT AVAILABLE",
        address: branchData.address,
        sales_representatives: branchData.sales_representatives || [],
        nin: branchData.nin || "NOT AVAILABLE",
        // New columns from the branch details
        wallet: branchData.wallet || "NOT AVAILABLE",
        bank_account: branchData.bank_account || "NOT AVAILABLE",
        wallet_balance: branchData.wallet_balance || "0.00",
        incentive_balance: branchData.incentive_balance || "0.00",
        float_balance: branchData.float_balance || "0.00",
        expense_balance: branchData.expense_balance || "0.00",
        notification_status: branchData.notification_status || false,
        two_factor_enabled: branchData.two_factor_enabled || false,
        created_at: branchData.created_at,
      };

      setBranchDetails(mappedData);
    }
  }, [branchData]);



  // Safe accessor functions
  const getSafeValue = (value, fallback = "Not Available") => {
    return value || fallback;
  };

  const getSafeArray = (array, fallback = []) => {
    return Array.isArray(array) && array.length > 0 ? array : fallback;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not Available";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Card Component
  const Card = ({ title, children }) => (
    <div className="bg-white shadow-md rounded-2xl p-5 border border-gray-100">
      <h2 className="text-lg font-semibold text-darkGreen3 mb-4 border-b pb-2">
        {title}
      </h2>
      <div className="space-y-2">{children}</div>
    </div>
  );

  // InfoRow Component
  const InfoRow = ({ label, value, badge, highlight }) => (
    <div className="flex justify-between items-center py-1">
      <span className="text-gray-500 font-medium">{label}</span>
      {badge ? (
        <span
          className={`px-2 py-1 text-xs rounded-full ${value === "ACTIVE" || value === "Activated"
            ? "bg-darkGreen3 text-lGrey"
            : "bg-dash3 text-lGrey"
            }`}
        >
          {value}
        </span>
      ) : (
        <span
          className={`text-gray-800 ${highlight ? "font-semibold text-darkGreen3" : ""
            }`}
        >
          {value || "—"}
        </span>
      )}
    </div>
  );

  return (
    <MainLayouts>
      <div className="relative sm:rounded-lg py-6 w-full">
        <section className="w-full pr-[3%] pt-3 my-3 bg-gray-100">
          <nav className="bg-gray-200 py-0 px-4 rounded-lg">
            <ol className="list-none p-0 inline-flex mb-2">
              <li className="flex items-center">
                <p className="text-darkGreen3 pr-2">Operations Branch</p>
                <ChevronRightIcon />
              </li>
              <li className="flex items-center">
                <p className="text-orange1">Details</p>
              </li>
            </ol>
          </nav>
        </section>

        {loading ? (
          <Spinner loading={loading} />
        ) : (
          <section className="text-gray-600 body-font">
            <div className="container">
              <div className="flex justify-end mb-3">
                <Link
                  to="/operations/branch/list"
                  className="w-full sm:w-auto text-dGrey3 bg-darkGreen3 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                >
                  <span className="flex justify-center sm:justify-start mx-2">
                    <ArrowLeftSquare className="mr-2" />
                    Back
                  </span>
                </Link>
              </div>

              <section className="text-gray-600 body-font px-4 lg:px-8 pb-10">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* LEFT SIDE */}
                  <div className="lg:col-span-2 space-y-6">
                    {/* Branch Overview */}
                    <Card title="Branch Overview">
                      <InfoRow
                        label="Branch Name"
                        value={branchDetails.name?.toUpperCase()}
                      />
                      <InfoRow label="Email" value={branchDetails.email} />
                      <InfoRow
                        label="Status"
                        value={branchDetails.status?.toUpperCase()}
                        badge
                      />
                      <InfoRow
                        label="Activation"
                        value={
                          branchDetails.is_activated
                            ? "Activated"
                            : "Deactivated"
                        }
                      />
                      <InfoRow
                        label="Created By"
                        value={branchDetails.creator?.fullname}
                      />
                      <InfoRow
                        label="Created Date"
                        value={formatDate(branchDetails.created_at)}
                      />
                    </Card>

                    {/* Location & Contact */}
                    <Card title="Location & Contact">
                      <InfoRow label="State" value={branchDetails.state} />
                      <InfoRow label="City" value={branchDetails.city} />
                      <InfoRow label="Address" value={branchDetails.address} />
                      <InfoRow
                        label="Phone"
                        value={branchDetails.phone_number}
                      />
                      <InfoRow label="NIN" value={branchDetails.nin} />
                    </Card>

                    {/* Branch Management */}
                    <Card title="Branch Management">
                      <InfoRow
                        label="Managers"
                        value={
                          branchDetails.sales_representatives?.length > 0
                            ? branchDetails.sales_representatives.join(", ")
                            : "Not Assigned"
                        }
                      />
                    </Card>
                  </div>

                  {/* RIGHT SIDE */}
                  <div className="lg:col-span-1 space-y-6">
                    <Card title="Wallet Information">

                      <InfoRow
                        label="Wallet"
                        value={CurrencyFormatter(branchDetails.wallet_balance)}
                      />
                      <InfoRow
                        label="Incentive"
                        value={CurrencyFormatter(
                          branchDetails.incentive_balance
                        )}
                      />
                      <InfoRow
                        label="Float"
                        value={CurrencyFormatter(branchDetails.float_balance)}
                      />
                      <InfoRow
                        label="Expense"
                        value={CurrencyFormatter(branchDetails.expense_balance)}
                      />
                      <span>Total Balance :</span>
                      <InfoRow
                        // label="Total Balance"
                        value={CurrencyFormatter(
                          branchDetails.wallet?.total_balance
                        )}
                        highlight
                      />
                    </Card>

                    <Card title="Bank Account">
                      <InfoRow
                        label="Bank Name"
                        value={branchDetails.bank_account?.bank_name}
                      />
                      <InfoRow
                        label="Account Number"
                        value={branchDetails.bank_account?.account_number}
                      />
                      <InfoRow
                        label="Account Name"
                        value={branchDetails.bank_account?.account_name}
                      />
                      <InfoRow
                        label="Bank Code"
                        value={branchDetails.bank_account?.bank_code}
                      />
                    </Card>
                  </div>
                </div>
              </section>
            </div>
          </section>
        )}
      </div>
    </MainLayouts>
  );
};

export default BranchDetails;
